/**
 * Web push rules shared by the console, the API Worker and (mirrored) the database (ADR 0019).
 * Pure functions: no I/O.
 */

/** At most this many devices (push subscriptions) per user; the database enforces the same limit. */
export const PUSH_MAX_DEVICES = 10;

/** Device label shown in the console ("Chrome · Android"); never the full user agent string. */
export const PUSH_LABEL_MAX = 100;

/**
 * Push services the Worker may POST to. A subscription endpoint is user input: without this list a user
 * could make the Worker send signed requests to any URL. Mirrored by fts_push_endpoint_ok() in SQL.
 *   FCM (Chrome, Edge on Android, Samsung Internet, Opera), Mozilla autopush (Firefox),
 *   Apple (Safari on macOS/iOS 16.4+), WNS (Edge on Windows).
 */
export const PUSH_ENDPOINT_RE =
  /^https:\/\/(fcm\.googleapis\.com|android\.googleapis\.com|([a-z0-9-]+\.)*push\.services\.mozilla\.com|([a-z0-9-]+\.)*push\.apple\.com|([a-z0-9-]+\.)*notify\.windows\.com)\/[\x21-\x7e]*$/;
export const PUSH_ENDPOINT_MAX = 1024;

export function isPushEndpointAllowed(endpoint: string): boolean {
  return typeof endpoint === "string" && endpoint.length <= PUSH_ENDPOINT_MAX && PUSH_ENDPOINT_RE.test(endpoint);
}

/** base64url without padding: p256dh = 65-byte uncompressed P-256 point, auth = 16-byte secret. */
export const PUSH_P256DH_RE = /^[A-Za-z0-9_-]{87}$/;
export const PUSH_AUTH_RE = /^[A-Za-z0-9_-]{22}$/;

export type PushSubscriptionInput = { endpoint: string; p256dh: string; auth: string; label: string | null };

/** Client-side check before the database does the same (invalid_push_subscription). */
export function validPushSubscription(s: PushSubscriptionInput): boolean {
  return isPushEndpointAllowed(s.endpoint) && PUSH_P256DH_RE.test(s.p256dh) && PUSH_AUTH_RE.test(s.auth) && (s.label === null || s.label.length <= PUSH_LABEL_MAX);
}

/** Alarm types pushed with Urgency: high and a 1-hour TTL; the rest normal urgency, 4 hours. */
export const PUSH_URGENT_TYPES = ["impact", "checklist_critical", "overload"] as const;
export const PUSH_TTL_URGENT_S = 3600;
export const PUSH_TTL_NORMAL_S = 4 * 3600;

export const isUrgentPushType = (type: string): boolean => (PUSH_URGENT_TYPES as readonly string[]).includes(type);
export const pushTtl = (type: string): number => (isUrgentPushType(type) ? PUSH_TTL_URGENT_S : PUSH_TTL_NORMAL_S);
export const pushUrgency = (type: string): "high" | "normal" => (isUrgentPushType(type) ? "high" : "normal");

/**
 * Retry delay of a push the service refused for now (429 / 5xx / network): exponential from 30 s,
 * at least the service's Retry-After, at most 30 minutes. `attempt` is 1-based.
 */
export function pushRetryDelayS(attempt: number, retryAfterS?: number | null): number {
  const exp = Math.min(30 * 2 ** Math.max(0, attempt - 1), 1800);
  return Math.min(Math.max(exp, Math.ceil(retryAfterS ?? 0)), 1800);
}

type GraphemeSegmenter = new (locale?: string, options?: { granularity: "grapheme" }) => { segment(text: string): Iterable<{ segment: string }> };

/**
 * Cut `s` to at most `max` UTF-16 units without splitting a character (QA-7B-07): whole grapheme clusters
 * (emoji with modifiers, flags) when Intl.Segmenter exists, otherwise whole code points.
 */
export function clipText(s: string, max: number): string {
  if (s.length <= max) return s;
  const Seg = (Intl as unknown as { Segmenter?: GraphemeSegmenter }).Segmenter;
  const parts: string[] = Seg ? Array.from(new Seg(undefined, { granularity: "grapheme" }).segment(s), (x) => x.segment) : Array.from(s);
  let out = "";
  for (const p of parts) {
    if (out.length + p.length > max) break;
    out += p;
  }
  return out;
}

/** Short device label from a user agent: browser · platform. Unknown parts are left out. */
export function pushDeviceLabel(ua: string): string {
  const browser =
    /EdgA?\//.test(ua) ? "Edge"
    : /SamsungBrowser\//.test(ua) ? "Samsung Internet"
    : /OPR\/|Opera/.test(ua) ? "Opera"
    : /Firefox\/|FxiOS\//.test(ua) ? "Firefox"
    : /Chrome\/|CriOS\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari"
    : null;
  const platform =
    /Android/.test(ua) ? "Android"
    : /iPhone|iPod/.test(ua) ? "iPhone"
    : /iPad/.test(ua) ? "iPad"
    : /Windows/.test(ua) ? "Windows"
    : /Mac OS X|Macintosh/.test(ua) ? "macOS"
    : /Linux/.test(ua) ? "Linux"
    : null;
  return [browser, platform].filter(Boolean).join(" · ").slice(0, PUSH_LABEL_MAX) || "—";
}
